/*
 * Project: bellcloud
 * File: components/ui/common/DeviceSummary.tsx
 * Description: Device summary component displaying device statistics (alarms, alerts, connected, total)
 */

import React from 'react';
import styled from '@emotion/styled';
import DashboardAlarmIcon from '/icons/dashboardAlarm.svg';
import DashboardAlertIcon from '/icons/dashboardAlert.svg';

interface DeviceSummaryProps {
  title: string;
  value: number;
  variant?: 'danger' | 'caution' | 'default';
  showIcon?: boolean;
  className?: string;
  onClick?: () => void;
}

const Container = styled.div<{ clickable?: boolean }>`
  display: flex;
  padding: ${({ theme }) => theme.spacing[7]}px;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[7]}px;
  flex: 1 0 0;
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;
  ${({ clickable, theme }) =>
    clickable &&
    `&:hover {
      border-color: ${theme.colors.border.interactive.hover};
      background-color: ${theme.colors.surface.interactive.hover};
    }`}
`;

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.lg}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 130%;
  margin: 0;
`;

const ValueContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
`;

const ValueText = styled.span<{ variant?: 'danger' | 'caution' | 'default' }>`
  color: ${({ theme, variant = 'default' }) => {
    switch (variant) {
      case 'danger':
        return theme.colors.status.danger.stroke.text;
      case 'caution':
        return theme.colors.status.caution.stroke.text;
      default:
        return theme.colors.text.primary;
    }
  }};
  font-family: Inter;
  font-size: ${({ theme }) => theme.fontSize.xl}px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: 130%;
`;

const IconImage = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

export const DeviceSummary: React.FC<DeviceSummaryProps> = ({
  title,
  value,
  variant = 'default',
  showIcon = false,
  className,
  onClick,
}) => {
  const getIconSrc = () => {
    if (variant === 'danger') return DashboardAlarmIcon;
    if (variant === 'caution') return DashboardAlertIcon;
    return null;
  };

  const iconSrc = getIconSrc();

  return (
    <Container className={className} clickable={!!onClick} onClick={onClick}>
      <Title>{title}</Title>
      <ValueContainer>
        {showIcon && iconSrc && <IconImage src={iconSrc} alt={`${title} icon`} />}
        <ValueText variant={variant}>{value}</ValueText>
      </ValueContainer>
    </Container>
  );
};
